/* eslint-disable react/no-unsafe */
/**
 * A text field with some extra bits and bobs to make validation and error
 * tasks easier for identity fields. Useful for username, email, password, etc.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import debounce from "lodash.debounce";
import * as React from "react";

import {Id} from "@khanacademy/wonder-blocks-core";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedTextField from "#components/text-inputs-package/deprecated-text-field.tsx";

import sharedStyles from "../shared-styles.ts";

import type {IdentityTextFieldProps, ValidationResult} from "./types.ts";

type Props = IdentityTextFieldProps;

type PropsWithForwardRef = Props & {
    forwardedRef: React.Ref<HTMLInputElement>;
};

type State = {
    focused: boolean;
    validation: ValidationResult;
    value: string | null;
};

type DefaultProps = {
    allowEmpty: PropsWithForwardRef["allowEmpty"];
    disabled: PropsWithForwardRef["disabled"];
    fieldType: PropsWithForwardRef["fieldType"];
    normalizer: PropsWithForwardRef["normalizer"];
    placeholder: PropsWithForwardRef["placeholder"];
    validator: PropsWithForwardRef["validator"];
};

class IdentityTextField extends React.Component<PropsWithForwardRef, State> {
    static defaultProps: DefaultProps = {
        allowEmpty: false,
        fieldType: "text",
        normalizer: (s: string) => s,
        placeholder: "",
        validator: (s: string) => null,
        disabled: false,
    };

    state: State = {
        focused: false,
        validation: null,
        value: null,
    };

    componentDidMount() {
        const value = this.props.initialValue || this.props.value;
        this._mounted = true;
        if (value) {
            this.handleValueChange(value);
        }
    }

    UNSAFE_componentWillUpdate(
        nextProps: PropsWithForwardRef,
        nextState: State,
    ) {
        const nextValue = nextProps.value;
        if (nextValue && nextProps.value !== this.props.value) {
            this.handleValueChange(nextValue);
        }
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    _mounted = false;

    validate(value: string) {
        const {allowEmpty, emptyMessage, validator} = this.props;
        const handleValidation = (validation: ValidationResult) => {
            const {onValidation} = this.props;
            if (onValidation) {
                onValidation(!validation, value, validation);
            }
            if (this._mounted) {
                this.setState({validation: validation});
            }
        };
        if (value === "" && !allowEmpty) {
            handleValidation(
                emptyMessage || t`This field cannot be left empty.`,
            );
        } else if (validator) {
            const validation = validator(value);
            if (validation instanceof Promise) {
                validation.then(handleValidation).catch((e: Error) => {
                    if (this._mounted) {
                        this.setState({
                            validation: t`An error occured while validating this field.`,
                        });
                    }
                });
            } else {
                handleValidation(validation);
            }
        }
    }

    // For validators that make server-side requests, ensure that you specify
    // the useDebounceValidate flag as part of IdentityTextFieldProps to make
    // use of this wrapper function. Using this approach ensures that the user
    // is given feedback on invalid inputs, while preventing bottlenecks caused
    // by calling this.validate too frequently on every onChange input event
    debouncedValidate: (value: string) => void = debounce(
        (v) => this.validate(v),
        250,
    );

    shouldShowError(): boolean {
        const {showValidationWhileFocused, error} = this.props;
        const {focused, validation} = this.state;
        return !!(
            error ||
            ((!focused || showValidationWhileFocused) && validation)
        );
    }

    handleValueChange: (value: string) => void = (value: string) => {
        const {normalizer, useDebouncedValidate} = this.props;
        if (normalizer) {
            value = normalizer(value);
        }
        this.setState({value});

        if (useDebouncedValidate) {
            this.debouncedValidate(value);
        } else {
            this.validate(value);
        }
    };

    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void = (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const {onChange} = this.props;
        if (onChange) {
            onChange(e);
        }
        this.handleValueChange(e.target.value);
    };

    handleFocus: (e: React.FocusEvent<HTMLInputElement>) => void = (
        e: React.FocusEvent<HTMLInputElement>,
    ) => {
        const {onFocus} = this.props;
        this.setState({focused: true});
        if (onFocus) {
            onFocus(e);
        }
    };

    handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void = (
        e: React.FocusEvent<HTMLInputElement>,
    ) => {
        // We need to clear any stale validation messages, otherwise it
        // there is no guarantee that the error message displayed will be
        // accurate until the validation function resolves.
        this.setState({focused: false, validation: null});
        this.validate(e.currentTarget.value);

        const {onBlur} = this.props;
        if (onBlur) {
            onBlur(e);
        }
    };

    _formatStringForId: (
        node: Exclude<React.ReactNode, null | undefined>,
    ) => string = (node) => {
        // The label may be an array, so we need to convert it to a string before
        // modifying it. The UniqueIdFactory doesn't want any special characters,
        // so we filter those out and format the string to match standard ids.
        return node
            .toString()
            .toLowerCase()
            .replace(/[^a-zA-Z0-9 ]/g, "")
            .replace(/ /g, "-");
    };

    render(): React.ReactNode {
        const {
            error,
            fieldType,
            label,
            maxLength,
            notice,
            placeholder,
            suggestion,
            testId,
            disabled,
            forwardedRef,
            labelStyle,
        } = this.props;
        const {value, validation, focused} = this.state;
        const showError = this.shouldShowError();
        const bottomContainerClass = showError
            ? sharedStyles.fieldError
            : suggestion
              ? sharedStyles.fieldWarning
              : sharedStyles.hiddenFieldError;

        const fieldRequirementText = error || validation || suggestion;
        return (
            <Id>
                {(idRoot) => {
                    const inputId = `${idRoot}input`;
                    const fieldRequirementId = `${idRoot}fieldRequirement`;
                    return (
                        <div
                            className={css(
                                sharedStyles.fieldWrapper,
                                sharedStyles.label,
                            )}
                        >
                            <LabelLarge
                                style={[styles.label, labelStyle]}
                                htmlFor={inputId}
                                tag="label"
                            >
                                {label}
                            </LabelLarge>
                            {notice && (
                                <div
                                    className={css(
                                        sharedStyles.fieldRequirement,
                                    )}
                                >
                                    {notice}
                                </div>
                            )}
                            <DeprecatedTextField
                                className={css(
                                    sharedStyles.textInput,
                                    showError && sharedStyles.errorInput,
                                    showError &&
                                        !focused &&
                                        sharedStyles.errorInputShake,
                                )}
                                type={fieldType}
                                placeholder={placeholder}
                                maxLength={maxLength}
                                value={value || ""}
                                width="100%"
                                onFocus={this.handleFocus}
                                onChange={this.handleChange}
                                onBlur={this.handleBlur}
                                testId={testId}
                                id={inputId}
                                invalid={showError ? "true" : "false"}
                                aria-describedby={fieldRequirementId}
                                disabled={disabled}
                                ref={forwardedRef}
                            />
                            <div
                                className={css(
                                    sharedStyles.fieldRequirementContainer,
                                )}
                            >
                                <div
                                    aria-live="polite"
                                    id={fieldRequirementId}
                                    className={css(
                                        sharedStyles.fieldRequirement,
                                        bottomContainerClass,
                                    )}
                                >
                                    {fieldRequirementText}
                                </div>
                            </div>
                        </div>
                    );
                }}
            </Id>
        );
    }
}

const styles = StyleSheet.create({
    label: {
        marginBottom: 0,
    },
});

export default React.forwardRef<HTMLInputElement, Props>((props, ref) => (
    <IdentityTextField {...props} forwardedRef={ref} />
));
